import { erc20Abi } from 'viem'
import {
  InverseMarketAbi,
  InverseEscrowAbi,
  InverseDbrAbi,
  CoolerMonoAbi,
  FlyingTulipLendingLensAbi,
  ResupplyPairAbi,
  LlamaLendControllerAbi,
  LlamaLendAmmAbi,
  CurvanceMarketManagerAbi,
  CurvanceCTokenAbi,
  TwyneCollateralVaultAbi,
  FraxlendPairAbi,
} from '@1delta/abis'
import { Lender } from '@1delta/lender-registry'
import {
  isAaveV2Type,
  isAaveV3Type,
  isAaveV4Type,
  isCompoundV3,
  isEulerType,
  isFluid,
  isGearboxV3,
  isInit,
  isDolomite,
  isAaveV32Type,
  isYLDR,
  isMorphoType,
  isMidnight,
  isTerm,
  isExactly,
  isLiquityFamily,
  isRiver,
  isTeller,
  isTermMax,
  isInverse,
  isCooler,
  isFlyingTulip,
  isLlamaLend,
  isDssType,
  isFrankencoin,
  isResupply,
  isFraxlend,
  isCurvance,
  isTwyne,
  isCompoundV2Type,
  isSiloV2Type,
  isSiloV3Type,
} from '../../utils'
import { IncentivesControllerAbi } from '../../abis/lendle/IncentivesController'
import { RewarderAbi } from '../../abis/aurelius/Rewarder'
import { AavePoolV3UpgradedAbi } from '../../abis/aave/AavePoolV3Upgraded'
import { AaveV3ProtocolDataProviderAbi } from '../../abis/aave/AaveV3ProtocolDataProvider'
import { InitLensAbi } from '../../abis/init/InitLens'
import { CometAbi } from '../../abis/compound-v3/Comet'
import { CometExtAbi } from '../../abis/compound-v3/CometExt'
import { AavePoolLegacyAbi } from '../../abis/aave-v3/AavePoolLegacy'
import { AavePoolDataProviderLegacyAbi } from '../../abis/aave-v3/AavePoolDataProviderLegacy'
import { AavePoolAndDataProviderAbi } from '../../abis/aave/AavePoolAndDataProvider'
import { VariableDebtTokenAbi } from '../../abis/aave/VariableDebtToken'
import { MultiFeeDistributionAbi } from '../../abis/lendle/MultiFeeDistribution'
import { PullRewardsIncentivesController } from '../../abis/meridian/PullRewardsIncentivesController'
import { CometRewardsAbi } from '../../abis/compound-v3/CometRewards'
import { YLDRAbi } from '../../abis/aave-v3/YLDR'
import {
  MorphoBlueAbi,
  MorphoLensAbi,
  AaveV4SpokeAbi,
  FluidVaultResolverAbi,
  GearboxCreditAccountCompressorV310Abi,
  MidnightAbi,
  TermRepoTokenAbi,
  TermRepoServicerAbi,
  TermRepoCollateralManagerAbi,
  ExactlyPreviewerAbi,
  LiquityTroveManagerAbi,
  LiquityStabilityPoolAbi,
  LiquityTroveNFTAbi,
  LiquityCollSurplusPoolAbi,
  RiverTroveManagerAbi,
  RiverStabilityPoolAbi,
  TellerV2Abi,
  TellerCollateralManagerAbi,
  TermMaxViewerAbi,
  UsddVatAbi,
  FrankencoinPositionAbi,
} from '@1delta/abis'
import { ComptrollerAbi } from '../../abis/compound-v2/Comptroller'
import { CompoundV2CollateralToken } from '../../abis/compound-v2/CollateralToken'
import { accountLensAbi } from '../../abis/euler/accountLens'
import { SiloAbi } from '../../abis/silo-v2/Silo'
import { DolomiteMarginAbi } from '@1delta/abis'

/** Minimal ERC-4626 fragment for the Resupply collateral-vault share price. */
const VAULT_CONVERT_ABI = [
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'shares', type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

/**
 * The Twyne user-data ABI: the collateral vault, plus ONE borrowed item.
 *
 * `convertToAssets` is not on the vault — a collateral vault is neither ERC-20
 * nor ERC-4626, its `fallback()` reverts `T_CV_OperationDisabled` — so the
 * market's RECEIPT token answers it. Without the item the call cannot even be
 * ENCODED, comes back as a failure, and the parser reads that as a 1:1 share
 * price, understating every position by the receipt's accrued yield.
 *
 * Only the ONE item is added, deliberately. Splicing in the whole wrapper ABI
 * re-declares `asset` / `balanceOf` / `decimals`, and a duplicated name makes
 * the encoder ambiguous — the `asset()` read then fails too, so a fix for one
 * silent error introduces two more. Merged ABIs are not free.
 */
const TWYNE_USER_ABI = [
  ...TwyneCollateralVaultAbi,
  {
    name: 'convertToAssets',
    type: 'function',
    stateMutability: 'view',
    inputs: [{ name: 'shares', type: 'uint256' }],
    outputs: [{ type: 'uint256' }],
  },
] as const

export const getAbi = (lender: Lender) => {
  if (isAaveV4Type(lender as any)) return [...AaveV4SpokeAbi]
  if (isAaveV2Type(lender as any))
    return [
      ...AavePoolAndDataProviderAbi,
      ...VariableDebtTokenAbi,
      ...RewarderAbi,
      ...PullRewardsIncentivesController,
      ...IncentivesControllerAbi,
      ...MultiFeeDistributionAbi,
    ]
  if (isYLDR(lender as any)) return [...VariableDebtTokenAbi, ...YLDRAbi]

  if (isAaveV32Type(lender as any))
    return [...AavePoolV3UpgradedAbi, ...AaveV3ProtocolDataProviderAbi]

  if (isAaveV3Type(lender as any))
    return [
      ...AavePoolLegacyAbi,
      ...VariableDebtTokenAbi,
      ...AavePoolDataProviderLegacyAbi,
    ]
  if (isCompoundV3(lender as any))
    return [...CometAbi, ...CometExtAbi, ...CometRewardsAbi]
  if (isMorphoType(lender)) return [...MorphoBlueAbi, ...MorphoLensAbi]
  if (isMidnight(lender)) return [...MidnightAbi] as any
  // Term positions span three serial contracts (repoToken / servicer /
  // collateralManager). Function names don't collide, so a single merged ABI
  // resolves each call by name while each call targets its own address.
  if (isTerm(lender))
    return [
      ...TermRepoTokenAbi,
      ...TermRepoServicerAbi,
      ...TermRepoCollateralManagerAbi,
    ] as any
  // Exactly user data is a single Previewer.exactly(account) call.
  if (isExactly(lender)) return [...ExactlyPreviewerAbi] as any
  // Liquity positions span four branch contracts (TroveManager / TroveNFT /
  // StabilityPool / CollSurplusPool). Function names we call don't collide,
  // so a single merged ABI resolves each call by name while each call
  // targets its own address (same trick as Term).
  if (isLiquityFamily(lender))
    return [
      ...LiquityTroveManagerAbi,
      ...LiquityStabilityPoolAbi,
      ...LiquityTroveNFTAbi,
      ...LiquityCollSurplusPoolAbi,
    ] as any
  // River user data spans the per-market TroveManagers + the diamond's SP
  // facet — merged ABI, per-call addresses (same trick as Term/Liquity).
  if (isRiver(lender))
    return [...RiverTroveManagerAbi, ...RiverStabilityPoolAbi] as any
  // Teller user data spans TellerV2 (bid reads) + CollateralManager (collateral
  // amount) — merged ABI, per-call addresses (same trick as Term/Liquity/River).
  if (isTeller(lender))
    return [...TellerV2Abi, ...TellerCollateralManagerAbi] as any
  // TermMax user data is a single `TermMaxViewer.getPositionDetails` batch
  // read — FT/XT/collateral balances plus every GT sub-account in one call.
  if (isTermMax(lender)) return [...TermMaxViewerAbi] as any
  // Inverse user data spans the Markets + predicted escrows + DBR —
  // merged ABI, per-call addresses (same trick as Term/Liquity/River).
  if (isInverse(lender))
    return [...InverseMarketAbi, ...InverseEscrowAbi, ...InverseDbrAbi] as any
  // Cooler user data is a single `accountPosition` read on the MonoCooler.
  if (isCooler(lender)) return [...CoolerMonoAbi] as any
  // Flying Tulip user data is a single `userPortfolio` read on the LendingLens.
  if (isFlyingTulip(lender)) return [...FlyingTulipLendingLensAbi] as any
  // LlamaLend user data spans the Controller (state / health / band prices /
  // approval) and its AMM (occupied bands) — merged ABI, per-call addresses.
  // Only the SHARED controller surface is needed: nothing read per user
  // differs between the two generations.
  if (isLlamaLend(lender))
    return [...LlamaLendControllerAbi, ...LlamaLendAmmAbi] as any
  // dss user data (Sky + the USDD fork) reads only the Vat (urns + ilks) —
  // the CDP discovery walk decodes inside the build phase with its own
  // manager ABI. `Usdd*Abi` are plain dss ABIs; the name is historical.
  if (isDssType(lender)) return [...UsddVatAbi] as any
  // Frankencoin user data spans the per-user position contracts + the
  // collateral ERC-20 balance — merged ABI, per-call addresses.
  if (isFrankencoin(lender))
    return [...FrankencoinPositionAbi, ...erc20Abi] as any
  // Resupply user data spans the pairs (snapshot + accounting) and each pair's
  // ERC-4626 collateral vault (`convertToAssets`) — merged ABI, per-call
  // addresses. `erc20Abi` carries no `convertToAssets`, hence the local
  // fragment; there is no name collision with the pair surface.
  // Fraxlend user data is entirely on the pair contract (collateral, borrow
  // shares, fToken balance, previewAddInterest) — one ABI, per-call addresses.
  if (isFraxlend(lender)) return [...FraxlendPairAbi] as any
  if (isResupply(lender))
    return [...ResupplyPairAbi, ...VAULT_CONVERT_ABI] as any
  // Curvance user data spans the MarketManager (`accountAssets` for the hold
  // period, `statusOf` for the protocol's own valuation) and the two cTokens
  // (balances + `exchangeRate`) — merged ABI, per-call addresses, the same
  // trick as Term/Liquity. No name collisions between the two surfaces.
  if (isCurvance(lender))
    return [...CurvanceMarketManagerAbi, ...CurvanceCTokenAbi] as any
  // Twyne user data is mostly on the COLLATERAL VAULT — one contract per
  // position, so one ABI with per-call addresses. The factory read that
  // discovers those vaults happens in the build phase, not in this batch.
  //
  // The wrapper ABI is here for exactly ONE call: `convertToAssets` on the
  // market's RECEIPT token, which the vault itself does not implement (it is
  // neither ERC-20 nor ERC-4626 — `fallback()` reverts). Without it the call
  // cannot even be ENCODED, comes back `'0x'`, and the parser reads that as a
  // 1:1 share price — understating every Euler-side position by the receipt's
  // accrued yield (0.53 % today, and rising) with nothing to show for it.
  if (isTwyne(lender)) return TWYNE_USER_ABI as any
  if (isInit(lender as any)) return InitLensAbi
  if (isEulerType(lender as any)) return accountLensAbi as any
  if (isCompoundV2Type(lender as any))
    return [...ComptrollerAbi, ...CompoundV2CollateralToken]
  if (isSiloV2Type(lender as any) || isSiloV3Type(lender as any))
    return [...SiloAbi]
  if (isFluid(lender as any)) return [...FluidVaultResolverAbi] as any
  if (isGearboxV3(lender as any))
    return [...GearboxCreditAccountCompressorV310Abi] as any
  if (isDolomite(lender as any)) return [...DolomiteMarginAbi] as any
  return []
}
